import {
    Button,
    Flex,
    Heading, HStack, Stack, Text, VStack,
} from '@chakra-ui/react';
import { FaIcon, FaIconProps, PropertyDetailsLayout } from '@acp/core-ui';
import {
    faBuilding, faCloud, faEnvelope, faSliders, faUserCircle, faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { ComponentProps } from 'react';
import { Link, Outlet, useMatch } from 'react-router-dom';
import { useSession } from '@acp/feature/auth';
import { CardLayout } from '../dashboard';

function MenuItem({
    icon, text, to, ...props
}: {
    icon: FaIconProps['icon'];
    text: string;
    to: string;
} & ComponentProps<typeof Button>) {
    const matches = useMatch({
        path: to,
    });

    return (
        <Button
            as={Link}
            to={to}
            variant="ghost"
            colorScheme="charcoal"
            justifyContent="flex-start"
            color="muted.500"
            fontWeight="medium"
            px={5}
            _hover={{
                bgColor: 'muted.25',
                color: 'muted.600',
            }}
            _active={{
                color: 'black',
                borderRightColor: 'primary.500',
                borderRightWidth: '3px',
                roundedEnd: 'none',
                _hover: {
                    bgColor: 'white',
                },
            }}
            isActive={matches !== null}
            {...props}
        >
            <HStack gap={1}>
                <Flex w={1} justifyContent="center">
                    <FaIcon icon={icon} />
                </Flex>
                <Text ml={4}>{text}</Text>
            </HStack>
        </Button>
    );
}

export function SettingsPage() {
    const {
        complianceAgencyId,
        complianceSystemUrl,
        complianceUserId,
        isComplianceAgencyUserMaster,
    } = useSession();

    return (
        <PropertyDetailsLayout>
            <PropertyDetailsLayout.Content>
                <PropertyDetailsLayout.Title>
                    <PropertyDetailsLayout.Title.Header mt={3}>
                        Settings
                    </PropertyDetailsLayout.Title.Header>
                </PropertyDetailsLayout.Title>
                <PropertyDetailsLayout.Body>
                    <Stack direction={['column', null, null, 'row']} w="full" spacing={6}>
                        <Stack direction={['column', null, 'row', 'column']} spacing={6}>
                            <CardLayout bgColor="white" w={['full', null, null, 'xs']} maxH="fit-content">
                                <Heading size="md">
                                    My settings
                                </Heading>
                                <VStack w="full" align="left">
                                    <MenuItem
                                        icon={faUserCircle}
                                        text="Profile and security"
                                        as={Link}
                                        to={`${complianceSystemUrl}/Users/edit/${complianceUserId}`}
                                    />
                                    <MenuItem
                                        icon={faSliders}
                                        text="My preferences"
                                        as={Link}
                                        to="/settings/preferences"
                                    />
                                </VStack>
                            </CardLayout>

                            <CardLayout bgColor="white" maxW={['full', null, null, 'xs']} maxH="fit-content">
                                <Heading size="md">
                                    Agency settings
                                </Heading>
                                <VStack w="full" align="left">
                                    <MenuItem
                                        icon={faBuilding}
                                        text="Office profile"
                                        as={Link}
                                        to={`${complianceSystemUrl}/Agencies/editDetails/${complianceAgencyId}`}
                                    />
                                    <MenuItem
                                        icon={faCloud}
                                        text="API Integration"
                                        as={Link}
                                        to={`${complianceSystemUrl}/Integration/setup`}
                                    />
                                    {isComplianceAgencyUserMaster && (
                                        <MenuItem
                                            icon={faUsers}
                                            text="Manage users"
                                            as={Link}
                                            to={`${complianceSystemUrl}/Users`}
                                        />
                                    )}
                                    <MenuItem
                                        icon={faEnvelope}
                                        text="Notification preferences"
                                        as={Link}
                                        to="/settings/notification-preferences"
                                    />
                                </VStack>
                            </CardLayout>
                        </Stack>

                        <CardLayout bgColor="white" w="full" maxH="fit-content">
                            <Outlet />
                        </CardLayout>
                    </Stack>
                </PropertyDetailsLayout.Body>
            </PropertyDetailsLayout.Content>
        </PropertyDetailsLayout>
    );
}
